/**
 * Responsibility: let the sidebar shrink to its icons, without editing the kit.
 *
 * The kit's `SideMenu` has one width and no notion of collapsing. It is a
 * faithful copy of an upstream template and has to stay re-syncable, so this
 * narrows it from outside instead: a wrapper carries a data attribute and the
 * styles below reach the drawer through MUI's own class names.
 *
 * That is a deliberate trade. Reaching into another component's markup is
 * normally the wrong answer, and it is the right one here only because the
 * alternative - a local edit to a vendored file - is a change that silently
 * disappears the next time the kit is re-synced, taking the feature with it.
 *
 * Collapsed still shows the icons and the active highlight, because a sidebar
 * with no indication of where you are is a worse sidebar than a wide one.
 */
import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { drawerClasses } from '@mui/material/Drawer';
import { listItemIconClasses, listItemTextClasses, typographyClasses } from '@mui/material';

/** Wide enough for an icon and its touch target, and nothing else. */
const COLLAPSED_WIDTH = 60;

export function SidebarCollapseButton({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <Tooltip title={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}>
      <IconButton
        size="small"
        onClick={onToggle}
        aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
        sx={{
          // Only offered where there is a permanent sidebar to collapse. On
          // a phone it is a drawer that is already hidden.
          display: { xs: 'none', md: 'inline-flex' },
          transform: collapsed ? 'rotate(180deg)' : 'none',
          transition: 'transform 150ms',
        }}
      >
        <MenuOpenIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export function SidebarCollapseScope({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      data-sidebar={collapsed ? 'collapsed' : 'open'}
      sx={{
        display: 'contents',
        // Desktop only: the mobile drawer is an overlay and collapsing it
        // would leave a strip of icons over the page.
        '@media (min-width: 900px)': {
          [`&[data-sidebar="collapsed"] .${drawerClasses.paper}`]: {
            width: COLLAPSED_WIDTH,
            overflowX: 'hidden',
          },
          // The nav element reserves the row's width, so it has to shrink
          // too or the content keeps the old gap.
          '&[data-sidebar="collapsed"] > nav, &[data-sidebar="collapsed"] nav > .MuiDrawer-root': {
            width: COLLAPSED_WIDTH,
          },
          // **Every piece of text, rather than a list of specific ones.**
          // The kit renders section headings, the brand and the build footer
          // as plain Typography with nothing to hook onto, and a rule naming
          // each of them would break the next time the kit is re-synced.
          // Hiding text and keeping icons is also just what a collapsed
          // sidebar looks like.
          [`&[data-sidebar="collapsed"] .${drawerClasses.paper} .${typographyClasses.root}`]: {
            display: 'none',
          },
          [`&[data-sidebar="collapsed"] .${listItemTextClasses.root}`]: { display: 'none' },
          // The icon keeps its own centring once the label is gone.
          [`&[data-sidebar="collapsed"] .${listItemIconClasses.root}`]: {
            minWidth: 0,
            justifyContent: 'center',
          },
        },
      }}
    >
      {children}
    </Box>
  );
}
